import { ProRace, ProRaceStage } from '@radevents/shared';

import { SITE_ORIGIN } from '../config/siteOrigin';

export function stageLabel(stage: ProRaceStage): string {
  return stage.stageNumber === 0 ? 'Prolog' : `${stage.stageNumber}. Etappe`;
}

export function stageRouteText(stage: ProRaceStage): string {
  const cities = [stage.startCity, stage.endCity].filter(Boolean) as string[];
  const unique = cities.filter((c, i) => cities.indexOf(c) === i);
  return unique.join(' – ');
}

export function buildSportsEventSchema(race: ProRace): object {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    name: race.name,
    sport: 'Cycling',
    url: `${SITE_ORIGIN}/rennkalender/${race.slug}`,
    startDate: race.startDate,
    endDate: race.endDate,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
  };

  if (race.raceType === 'ETAPPENRENNEN') {
    schema['additionalType'] = 'https://en.wikipedia.org/wiki/Stage_race';
  } else if (race.raceType === 'EINTAGESRENNEN') {
    schema['additionalType'] = 'https://en.wikipedia.org/wiki/One-day_race';
  }

  const descParts: string[] = [];
  if (race.category) descParts.push(race.category);
  if (race.broadcastInfo) descParts.push(`Übertragung: ${race.broadcastInfo}`);
  if (race.description) descParts.push(race.description);
  if (descParts.length > 0) schema['description'] = descParts.join(' · ');

  if (race.websiteUrl) schema['sameAs'] = race.websiteUrl;

  if (race.winner) {
    schema['winner'] = { '@type': 'Person', name: race.winner };
  }

  return schema;
}

export function buildStageSchema(race: ProRace, stage: ProRaceStage): object {
  const label = stageLabel(stage);
  const route = stageRouteText(stage);
  const url = `${SITE_ORIGIN}/rennkalender/${race.slug}/etappe/${stage.stageNumber}`;
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    name: `${race.name} – ${label}${route ? `: ${route}` : ''}`,
    sport: 'Cycling',
    url,
    startDate: stage.stageDate ?? race.startDate,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    superEvent: {
      '@type': 'SportsEvent',
      name: race.name,
      url: `${SITE_ORIGIN}/rennkalender/${race.slug}`,
    },
  };

  if (stage.startCity) {
    schema['location'] = { '@type': 'Place', name: stage.startCity, address: stage.startCity };
  }

  const descParts: string[] = [];
  if (route) descParts.push(`Strecke: ${route}`);
  if (stage.distanceKm != null) descParts.push(`${stage.distanceKm} km`);
  if (stage.elevationMeters != null) descParts.push(`${stage.elevationMeters} Höhenmeter`);
  if (race.broadcastInfo) descParts.push(`Übertragung: ${race.broadcastInfo}`);
  if (stage.description) descParts.push(stage.description);
  if (descParts.length > 0) schema['description'] = descParts.join(' · ');

  if (stage.winner) {
    schema['winner'] = { '@type': 'Person', name: stage.winner };
  }

  return schema;
}

export function buildStageBreadcrumbSchema(race: ProRace, stage: ProRaceStage): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Rennkalender', item: `${SITE_ORIGIN}/rennkalender` },
      { '@type': 'ListItem', position: 2, name: race.name, item: `${SITE_ORIGIN}/rennkalender/${race.slug}` },
      { '@type': 'ListItem', position: 3, name: stageLabel(stage), item: `${SITE_ORIGIN}/rennkalender/${race.slug}/etappe/${stage.stageNumber}` },
    ],
  };
}

export function buildRaceCalendarListSchema(races: ProRace[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Profi-Rennkalender 2026',
    description: "Die wichtigsten Profi-Radrennen der Saison 2026 – Tour de France, Giro d'Italia, Vuelta und mehr.",
    url: `${SITE_ORIGIN}/rennkalender`,
    itemListElement: races.map((race, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: race.name,
      url: `${SITE_ORIGIN}/rennkalender/${race.slug}`,
    })),
  };
}
